import { MfaRequiredDetails } from '@aws-amplify/auth/src/types/Auth';
import { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { Amplify, Auth } from 'aws-amplify';
import forIn from 'lodash/forIn';
import startsWith from 'lodash/startsWith';
import getConfig from 'next/config';
import { identify, reportEvent } from 'utils/analytics';
import { clearCookies, getCookie, removeCookie, setCookie } from 'utils/cookies';
import { isBrowser } from 'utils/domUtils';
import { navigateToUrl } from 'utils/navigateUtils';
import { getObjProperty, toBoolean } from 'utils/object';

Amplify.Logger.LOG_LEVEL = 'INFO';

export interface IAuthDetails {
  isAuthenticated?: boolean;
  isApproved?: boolean;
  isManagement?: boolean;
  isSystem?: boolean;
  isSystemRW?: boolean;
  userId?: string;
  investorId?: string;
  sponsorId?: string;
  creatorId?: string;
  investorVerified?: boolean;
  investorAccredited?: boolean;
  investorPending?: boolean;
  groups?: string[];
  email?: string;
  emailVerified?: boolean;
  name?: string;
  nickname?: string;
  authToken?: string;
  kycDone?: boolean;
  isImpersonated?: boolean;
  impersonatingUser?: string;
}

export type IAuthData =
  | CognitoUser
  | MfaRequiredDetails
  | Record<string, unknown>
  | null
  | undefined;

export interface IImpersonateDetails {
  isApproved?: boolean;
  isManagement?: boolean;
  isSystem?: boolean;
  userId?: string;
  investorId?: string;
  sponsorId?: string;
  creatorId?: string;
  investorVerified?: boolean;
  investorAccredited?: boolean;
  investorPending?: boolean;
  groups?: string[];
  email?: string;
  emailVerified?: boolean;
  name?: string;
  nickname?: string;
  authToken?: string;
  kycDone?: boolean;
}

export interface IUserDetails {
  userId?: string;
  email?: string;
  name?: string;
  nickname?: string;
  verified?: boolean;
  accredited?: boolean;
  pending?: boolean;
  kycDone?: boolean;
  version?: number;
}

export const SIGNUP_COOKIE = 'sreSignUp';
export const USER_DETAILS_VERSION = 2;
const USER_DETAILS_KEY = 'sreUser';
const USER_IMPERSONATE_KEY = 'sreImpersonate';
const COGNITO_COOKIE_PREFIX = 'CognitoIdentityServiceProvider';

const { publicRuntimeConfig } = getConfig();
const {
  NEXT_PUBLIC_COGNITO,
  NEXT_PUBLIC_COGNITO_PUBLIC_KEYS,
  NEXT_PUBLIC_DOMAIN,
  NEXT_PUBLIC_COOKIE_DOMAIN,
  NEXT_PUBLIC_VERCEL_URL
} = publicRuntimeConfig;

export const isSystemUser = (groups?: string[]) => {
  return !!(groups && (groups.includes('system') || groups.includes('system_rw')));
};

export const isSystemRWUser = (groups?: string[]) => {
  return !!(groups && groups.includes('system_rw'));
};

export const isSponsorUser = (groups?: string[]) => {
  return !!(groups && groups.filter((group) => group.startsWith('sponsor_')).length > 0);
};

export const isManagementUser = (groups?: string[]) => {
  return isSystemUser(groups) || isSponsorUser(groups);
};

export const getGroups = (session?: CognitoUserSession | null) => {
  return getObjProperty(session?.getAccessToken()?.payload, 'cognito:groups');
};

export const getUserId = (session?: CognitoUserSession | null) => {
  return getObjProperty(session?.getIdToken()?.payload, 'user_id');
};

export const getInvestorId = (session?: CognitoUserSession | null) => {
  return getObjProperty(session?.getIdToken()?.payload, 'investor_id');
};

export const getSponsorId = (session?: CognitoUserSession | null) => {
  return getObjProperty(session?.getIdToken()?.payload, 'sponsor_id');
};

export const getCreatorId = (session?: CognitoUserSession | null) => {
  return getObjProperty(session?.getIdToken()?.payload, 'creator_id');
};

export const isInvestorVerified = (session?: CognitoUserSession | null) => {
  return (
    toBoolean(getObjProperty(session?.getIdToken()?.payload, 'investor_verified')) ||
    getVerifiedUserDetails(session)?.verified
  );
};

export const isInvestorPending = (session?: CognitoUserSession | null) => {
  return (
    toBoolean(getObjProperty(session?.getIdToken()?.payload, 'investor_pending')) ||
    getVerifiedUserDetails(session)?.pending
  );
};

export const isInvestorAccredited = (session?: CognitoUserSession | null) => {
  return (
    toBoolean(getObjProperty(session?.getIdToken()?.payload, 'investor_accredited')) ||
    getVerifiedUserDetails(session)?.accredited
  );
};

export const getName = (session?: CognitoUserSession | null) => {
  return getObjProperty(session?.getIdToken()?.payload, 'name');
};

export const getNickname = (session?: CognitoUserSession | null) => {
  return (
    getObjProperty(session?.getIdToken()?.payload, 'nickname') ||
    getVerifiedUserDetails(session)?.nickname
  );
};

export const getEmail = (session?: CognitoUserSession | null) => {
  return getObjProperty(session?.getIdToken()?.payload, 'email');
};

export const isEmailVerified = (session?: CognitoUserSession | null) => {
  return !!getObjProperty(session?.getIdToken()?.payload, 'email_verified');
};

export const getVerifiedUserDetails = (session?: CognitoUserSession | null) => {
  if (!isBrowser()) {
    return;
  }

  const email = getEmail(session);
  if (!email) {
    return;
  }

  const userData = getUserDetails();
  if (userData?.email !== email) {
    const impersonateDetails = getImpersonateDetails();
    if (userData?.email !== impersonateDetails?.email) {
      return;
    }
  }

  return userData;
};

export const getUserDetails = () => {
  if (!isBrowser()) {
    return;
  }

  const userDataStr = localStorage.getItem(USER_DETAILS_KEY);
  if (!userDataStr) {
    return;
  }

  return JSON.parse(userDataStr) as IUserDetails;
};

export const updateUserDetails = async (userDetails: IUserDetails) => {
  if (isBrowser()) {
    const userDetailsStr = JSON.stringify({ ...userDetails, version: USER_DETAILS_VERSION });
    localStorage.setItem(USER_DETAILS_KEY, userDetailsStr);
  }

  const user = await Auth.currentAuthenticatedUser();
  await Auth.updateUserAttributes(user, {
    name: userDetails?.name,
    nickname: userDetails?.nickname || userDetails?.name,
    investor_verified: String(!!userDetails.verified)
  });
};

export const getImpersonateDetails = () => {
  if (!isBrowser()) {
    return;
  }

  const impersonateDetails = getCookie(USER_IMPERSONATE_KEY);
  if (!impersonateDetails) {
    return;
  }

  return impersonateDetails as IImpersonateDetails;
};

export const setImpersonateDetails = (impersonateUser?: IImpersonateDetails) => {
  if (!isBrowser()) {
    return;
  }

  if (impersonateUser?.email) {
    setCookie(
      USER_IMPERSONATE_KEY,
      { ...impersonateUser },
      {
        domain: getCookiesDomain(),
        secure: getCookiesSecured(),
        path: '/'
      }
    );
  } else {
    removeCookie(USER_IMPERSONATE_KEY, getCookiesDomain(), getCookiesSecured());
  }
};

export const refreshUserDetails = async () => {
  return refreshToken(true);
};

export const clearAuthDetails = () => {
  if (!isBrowser()) {
    return;
  }

  clearCookies(COGNITO_COOKIE_PREFIX, getCookiesDomain(), getCookiesSecured());

  localStorage.removeItem(USER_DETAILS_KEY);

  forIn(window.localStorage, (value: string, objKey: string) => {
    if (startsWith(objKey, COGNITO_COOKIE_PREFIX)) {
      localStorage.removeItem(objKey);
    }
  });
};

export const cognitoConfig = NEXT_PUBLIC_COGNITO
  ? JSON.parse(NEXT_PUBLIC_COGNITO as string)
  : undefined;
export const cognitoKeys = NEXT_PUBLIC_COGNITO_PUBLIC_KEYS
  ? JSON.parse(NEXT_PUBLIC_COGNITO_PUBLIC_KEYS as string)?.keys
  : undefined;

export const isAuthConfigured = (): boolean => {
  return !!cognitoConfig;
};

export const initAuth = () => {
  if (cognitoConfig) {
    const amplifyConfig = {
      aws_project_region: cognitoConfig.REGION,
      aws_cognito_identity_pool_id: cognitoConfig.IDENTITY_POOL_ID,
      aws_cognito_region: cognitoConfig.REGION,
      aws_user_pools_id: cognitoConfig.USER_POOL_ID,
      aws_user_pools_web_client_id: cognitoConfig.APP_CLIENT_ID,

      // // Cognito Hosted UI configuration
      // oauth: {
      //   domain: cognitoConfig.DOMAIN,
      //   scope: ['phone', 'email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
      //   redirectSignIn: cognitoConfig.REDIRECT_SIGN_IN,
      //   redirectSignOut: cognitoConfig.REDIRECT_SIGN_OUT,
      //   responseType: 'code',
      //
      //   // optional, for Cognito hosted ui specified options
      //   options: {
      //     // Indicates if the data collection is enabled to support Cognito advanced offering features. By default, this flag is set to true.
      //     AdvancedSecurityDataCollectionFlag: true
      //   }
      // },
      federationTarget: 'COGNITO_USER_POOLS',
      Auth: {
        authenticationFlowType: 'USER_SRP_AUTH'
      },
      cookieStorage: {
        domain: getCookiesDomain(),
        expires: 365,
        path: '/',
        secure: getCookiesSecured()
      },
      ssr: true
    };

    console.log('initAuth - Amplify configure', amplifyConfig);
    Amplify.configure(amplifyConfig);

    console.log('initAuth - Auth configure');
    Auth.configure(amplifyConfig);

    const userDetails = getUserDetails();

    console.log('initAuth - refresh token');
    refreshToken(userDetails?.version !== USER_DETAILS_VERSION);
  }
};

export const getCookiesDomain = () => {
  return NEXT_PUBLIC_COOKIE_DOMAIN || NEXT_PUBLIC_VERCEL_URL || `.${NEXT_PUBLIC_DOMAIN}`;
};

export const getCookiesSecured = () => {
  return !(
    NEXT_PUBLIC_COOKIE_DOMAIN ||
    NEXT_PUBLIC_VERCEL_URL ||
    `.${NEXT_PUBLIC_DOMAIN}`
  )?.includes('localhost');
};

export const isAuthenticated = (session?: CognitoUserSession | null) => {
  return !!session;
};

export const getAuthDetails = (session?: CognitoUserSession | null) => {
  const authenticated = isAuthenticated(session);
  const groups = getGroups(session) || [];
  const systemUser = authenticated && isSystemUser(groups);
  const systemRWUser = authenticated && isSystemRWUser(groups);
  const impersonateUser = getImpersonateDetails();

  if (impersonateUser?.email) {
    return {
      ...impersonateUser,
      isApproved: !!impersonateUser?.isApproved,
      isManagement: !!impersonateUser?.isManagement,
      email: impersonateUser?.email || '',
      emailVerified: !!impersonateUser?.emailVerified,
      groups: impersonateUser?.groups || [],
      userId: impersonateUser?.userId || '',
      investorId: impersonateUser?.investorId || '',
      sponsorId: impersonateUser?.sponsorId || '',
      creatorId: impersonateUser?.creatorId || '',
      investorVerified: !!impersonateUser?.investorVerified,
      investorAccredited: !!impersonateUser?.investorAccredited,
      investorPending: !!impersonateUser?.investorPending,
      kycDone: !!impersonateUser?.kycDone,
      name: impersonateUser?.name || '',
      nickname: impersonateUser?.nickname || '',
      isAuthenticated: authenticated,
      isSystem: impersonateUser?.isSystem,
      isSystemRW: false,
      isImpersonated: true,
      impersonatingUser: getEmail(session) || ''
    } as IAuthDetails;
  }

  const managementUser = authenticated && isManagementUser(groups);
  const userId = getUserId(session) || '';
  const investorId = getInvestorId(session) || '';
  const sponsorId = getSponsorId(session) || '';
  const creatorId = getCreatorId(session) || '';
  const investorAccredited = toBoolean(authenticated && isInvestorAccredited(session)) as boolean;
  const investorVerified = toBoolean(authenticated && isInvestorVerified(session)) as boolean;
  const investorPending = toBoolean(authenticated && isInvestorPending(session)) as boolean;
  const approvedUser = toBoolean(
    authenticated && (systemUser || systemRWUser || managementUser || investorVerified)
  ) as boolean;
  const emailVerified = isEmailVerified(session);

  return {
    isAuthenticated: authenticated,
    isApproved: approvedUser,
    groups: groups,
    isManagement: managementUser,
    isSystem: systemUser,
    isSystemRW: systemRWUser,
    userId,
    investorId,
    sponsorId,
    creatorId,
    investorVerified,
    investorAccredited,
    investorPending,
    email: getEmail(session) || '',
    emailVerified,
    name: getName(session) || '',
    nickname: getNickname(session) || '',
    impersonateUser
  } as IAuthDetails;
};

export const verifyAuth = async (
  ssrAuthDetails?: IAuthDetails,
  clientAuthDetails?: IAuthDetails
) => {
  if (
    (ssrAuthDetails?.isApproved !== undefined &&
      !ssrAuthDetails?.isApproved &&
      clientAuthDetails?.isApproved) ||
    (ssrAuthDetails?.isAuthenticated !== undefined &&
      !ssrAuthDetails?.isAuthenticated &&
      clientAuthDetails?.isAuthenticated)
  ) {
    refreshToken(true);
  }
};

export const refreshToken = async (force = false) => {
  return Auth.currentSession()
    .then((session) => {
      const accessTokenExpire = session?.getAccessToken()?.getExpiration();
      const idTokenExpire = session?.getIdToken()?.getExpiration();
      const refreshToken = session.getRefreshToken();
      const currentTimeSeconds = Math.round(Date.now() / 1000);
      if (
        force ||
        accessTokenExpire - currentTimeSeconds < 43200 ||
        idTokenExpire - currentTimeSeconds < 43200
      ) {
        return Auth.currentAuthenticatedUser({ bypassCache: true }).then((user) => {
          return user.refreshSession(refreshToken, (err, data) => {
            if (err) {
              console.log('refreshToken - failed to fetch session', force);
              return signOut();
            } else {
              console.log('refreshToken - success', force);
            }
          });
        });
      }
    })
    .catch(() => {
      // No logged-in user: don't set auth header
    });
};

export const getHomeLink = () => 'https://www.usremsecurities.com';

export const navigateToHome = () => {
  if (isBrowser()) {
    return window.open(getHomeLink(), '_self');
  }
};

export const navigateToSignOut = () => {
  navigateToUrl('/connect/signOut');
};

export const signOut = async () => {
  reportEvent('sign_out');

  return Auth.signOut()
    .catch((err) => {
      console.warn('Sign out failed due to', err);
    })
    .finally(() => {
      clearAuthDetails();
    });
};

export const signIn = (
  email: string,
  password: string,
  onStateChange: (state: string, data?: IAuthData) => Promise<void>,
  onError: (err: Error | string) => void
) => {
  reportEvent('login', { method: 'email' });
  clearAuthDetails();

  return Auth.signIn(email.toLocaleLowerCase(), password)
    .then((user) => {
      if (user.challengeName === 'SMS_MFA' || user.challengeName === 'SOFTWARE_TOKEN_MFA') {
        console.log('confirm user with ' + user.challengeName);
        onStateChange('confirmSignIn', user);
      } else if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('require new password', user.challengeParam);
        onStateChange('requireNewPassword', user);
      } else if (user.challengeName === 'MFA_SETUP') {
        console.log('TOTP setup', user.challengeParam);
        onStateChange('TOTPSetup', user);
      } else if (
        user.challengeName === 'CUSTOM_CHALLENGE' &&
        user.challengeParam &&
        user.challengeParam.trigger === 'true'
      ) {
        console.log('custom challenge', user.challengeParam);
        onStateChange('customConfirmSignIn', user);
      } else {
        checkContact(user, onStateChange, onError);
      }
    })
    .catch((err) => {
      if (err.code === 'UserNotConfirmedException') {
        console.log('the user is not confirmed');
        onStateChange('confirmSignUp', { email });
      } else if (err.code === 'PasswordResetRequiredException') {
        console.log('the user requires a new password');
        onStateChange('forgotPassword', { email });
      } else {
        onError(err);
      }
    });
};

export const signUp = (
  email: string,
  password: string,
  sponsorId: string | undefined,
  onStateChange: (state: string, data?: IAuthData) => Promise<void>,
  onError: (err: Error | string) => void
) => {
  reportEvent('sign_up', { method: 'email' });
  clearAuthDetails();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const signup_info: any = {
    username: email.toLocaleLowerCase(),
    password: password,
    attributes: sponsorId
      ? {
          'custom:sponsor_id': sponsorId
        }
      : {},
    validationData: {}
  };

  Auth.signUp(signup_info)
    .then((result) => {
      console.log('signUp - success', result);
      onStateChange('confirmSignUp', { email, password, ...result });
    })
    .catch((err) => {
      onError(err);
    });
};

export const checkContact = (
  user: CognitoUser | undefined,
  onStateChange: (state: string, data?: IAuthData) => Promise<void>,
  onError: (err: Error | string) => void
) => {
  Auth.verifiedContact(user)
    .then(() => {
      onStateChange('signedIn', user);
    })
    .catch((err) => {
      onError(err);
    });
};

export const resendSignUp = (email: string, onError: (err: Error | string) => void) => {
  reportEvent('resendSignUp', { method: 'email' });

  Auth.resendSignUp(email.toLocaleLowerCase())
    .then(() => console.log('code resent'))
    .catch((err) => onError(err));
};

export const confirmSignUp = (
  email: string,
  code: string,
  authData: IAuthData,
  onStateChange: (state: string, data?: IAuthData) => Promise<void>,
  onError: (err: Error | string) => void
) => {
  reportEvent('confirmSignUp', { method: 'email' });

  Auth.confirmSignUp(email.toLocaleLowerCase(), code)
    .then(() => onStateChange('signedIn', authData))
    .catch((err) => onError(err));
};

export const getAuthUrl = (props?: {
  authUrl?: string;
  redirectUrl?: string;
  isAuthenticated?: boolean;
}) => {
  const baseAuthUrl =
    props?.authUrl || (props?.isAuthenticated ? '/connect/welcome' : '/connect/signUp');
  if (isBrowser()) {
    const currentUrlObj = new URL(window.location.href, window.location.href);
    const redirectUrlObj = new URL(
      `${baseAuthUrl}${currentUrlObj.search || ''}${currentUrlObj.hash || ''}`,
      window.location.href
    );
    redirectUrlObj.searchParams.delete('all');
    if (props?.redirectUrl) {
      redirectUrlObj.searchParams.append('url', props?.redirectUrl);
    }
    return redirectUrlObj.href;
  } else {
    return baseAuthUrl;
  }
};

export const navigateToAuthUrl = (props?: {
  authUrl?: string;
  redirectUrl?: string;
  isAuthenticated?: boolean;
}) => {
  return navigateToUrl(getAuthUrl(props));
};

export const authIdentify = (authDetails?: IAuthDetails) => {
  if (authDetails?.isAuthenticated) {
    const user_type = authDetails?.isSystem
      ? 'system'
      : authDetails?.isManagement
      ? 'management'
      : 'investor';
    identify(
      authDetails?.userId,
      authDetails?.nickname,
      authDetails?.email,
      user_type,
      !!authDetails?.creatorId
    );
  }
};
